import { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

// Simple global toast state (no external library needed)
type Listener = (toasts: ToastMessage[]) => void;
let toasts: ToastMessage[] = [];
const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l([...toasts]);
}

export const toast = {
  success: (message: string) => addToast('success', message),
  error: (message: string) => addToast('error', message),
  info: (message: string) => addToast('info', message),
  warning: (message: string) => addToast('warning', message),
};

function addToast(type: ToastType, message: string) {
  const id = crypto.randomUUID();
  toasts = [...toasts, { id, type, message }];
  notify();
  setTimeout(() => removeToast(id), 4000);
}

function removeToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  notify();
}

const iconMap: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠',
};

const colorMap: Record<ToastType, string> = {
  success: 'bg-green-900/80 border-green-700 text-green-100',
  error: 'bg-red-900/80 border-red-700 text-red-100',
  info: 'bg-blue-900/80 border-blue-700 text-blue-100',
  warning: 'bg-yellow-900/80 border-yellow-700 text-yellow-100',
};

export function ToastContainer() {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  useEffect(() => {
    listeners.add(setMessages);
    return () => {
      listeners.delete(setMessages);
    };
  }, []);

  if (messages.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full"
    >
      {messages.map((t) => (
        <div
          key={t.id}
          className={`
            flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg
            backdrop-blur-sm text-sm font-medium animate-in slide-in-from-right-4
            ${colorMap[t.type]}
          `}
        >
          <span className="mt-0.5 shrink-0">{iconMap[t.type]}</span>
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => removeToast(t.id)}
            className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
